import React, { useState, useEffect, useCallback } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import { useTheme } from "../theme/ThemeContext";
import { useSettings, formatAmount } from "../settings/SettingsContext";
import { PERIODS, isInPeriod, periodLabel } from "../utils/period";

import { API_BASE, apiFetch } from "../api";

const IncomeExpenseCard = () => {
  const { tokens, fonts } = useTheme();
  const { settings } = useSettings();
  const masked = settings.maskAmounts;
  const [period, setPeriod] = useState("Month");
  const [bills, setBills] = useState([]);
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      apiFetch(`${API_BASE}/bills`).then((res) => (res.ok ? res.json() : Promise.reject())),
      apiFetch(`${API_BASE}/income`).then((res) => (res.ok ? res.json() : Promise.reject())),
    ])
      .then(([billsData, incomeData]) => {
        setBills(billsData);
        setIncome(incomeData);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalIncome = income
    .filter((item) => isInPeriod(item.date, period))
    .reduce((sum, item) => sum + item.amount, 0);

  const totalExpenses = bills
    .filter((item) => isInPeriod(item.dueDate, period))
    .reduce((sum, item) => sum + item.amount, 0);

  const net = totalIncome - totalExpenses;
  const hasData = totalIncome > 0 || totalExpenses > 0;

  const chartData = [
    { name: "Income", value: totalIncome },
    { name: "Expenses", value: totalExpenses },
  ];

  return (
    <div style={cardStyle(tokens)}>
      <div style={cardRowStyle(tokens)}>
        <p style={{ ...styles.cardTitle, color: tokens.textPrimary, fontFamily: fonts.display }}>
          Income vs Expenses
        </p>
        <div style={styles.periodGroup}>
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                ...styles.periodBtn,
                backgroundColor: period === p ? tokens.accent : "transparent",
                color: period === p ? tokens.accentContrast : tokens.textMuted,
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: tokens.textMuted, fontSize: 13, margin: "12px 0" }}>Loading...</p>}
      {error && (
        <p style={{ color: tokens.negative, fontSize: 13, margin: "12px 0" }}>
          Couldn't reach the backend. Run it with `npm run server`.
        </p>
      )}

      {!loading && !error && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div style={{ width: 140, height: 140, position: "relative", flexShrink: 0 }}>
              {masked ? (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    border: `10px solid ${tokens.neutralSoft}`,
                  }}
                />
              ) : hasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={65}
                      label={false}
                    >
                      <Cell fill={tokens.positive} />
                      <Cell fill={tokens.negative} />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    border: `10px solid ${tokens.neutralSoft}`,
                  }}
                />
              )}
            </div>

            <div style={{ flex: 1, paddingLeft: 20, display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: tokens.textMuted }}>
                Net &middot; {periodLabel(period)}
              </span>
              <p
                style={{
                  ...styles.cardValue,
                  fontFamily: fonts.mono,
                  color: net >= 0 ? tokens.positive : tokens.negative,
                }}
              >
                {net >= 0 || masked ? "+" : "-"}{formatAmount(Math.abs(net), masked)}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                <span style={{ ...styles.subItem, color: tokens.textMuted }}>
                  <span style={{ color: tokens.positive, fontWeight: 900, marginRight: 6 }}>&middot;</span>
                  Income: {formatAmount(totalIncome, masked)}
                </span>
                <span style={{ ...styles.subItem, color: tokens.textMuted }}>
                  <span style={{ color: tokens.negative, fontWeight: 900, marginRight: 6 }}>&middot;</span>
                  Expenses: {formatAmount(totalExpenses, masked)}
                </span>
              </div>
            </div>
          </div>

          <div style={{ ...footerRowStyle(tokens), marginTop: 12 }}>
            <Link to="/transactions" style={{ ...styles.viewAllLink, color: tokens.accent }}>
              View All
            </Link>
            <div style={{ color: tokens.textMuted }}>{periodLabel(period)}</div>
          </div>
        </>
      )}
    </div>
  );
};

const cardStyle = (tokens) => ({
  backgroundColor: tokens.surface,
  border: `1px solid ${tokens.surfaceBorder}`,
  boxShadow: tokens.cardShadow,
  padding: 20,
  borderRadius: 16,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 16,
  transition: "background-color 0.2s ease, border-color 0.2s ease",
});

const cardRowStyle = (tokens) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  borderBottom: `1px solid ${tokens.surfaceBorder}`,
  paddingBottom: 8,
});

const footerRowStyle = (tokens) => ({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  borderTop: `1px solid ${tokens.surfaceBorder}`,
  paddingTop: 12,
  fontSize: 12,
});

const styles = {
  cardTitle: {
    fontSize: 15.5,
    fontWeight: "700",
    margin: 0,
  },
  cardValue: {
    fontSize: 22,
    fontWeight: "700",
    margin: "4px 0 8px 0",
  },
  subItem: {
    fontSize: 13,
    margin: 0,
  },
  periodGroup: {
    display: "flex",
    gap: 4,
  },
  periodBtn: {
    border: "none",
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
  },
  viewAllLink: {
    fontWeight: "600",
    textDecoration: "none",
  },
};

export default IncomeExpenseCard;
